import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Leaf, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { api } from "../lib/api";

export default function HomePage() {
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    api.get("/products/bestsellers").then((r) => setBestsellers(r.data)).catch(console.error);
  }, []);

  return (
    <div>
      <SEO
        title="Mimes Gourmet · Mermeladas artesanas del Pirineo"
        description="Mermeladas artesanas del Pirineo elaboradas en lotes pequeños en Graus (Huesca). 70% fruta, sin conservantes ni colorantes. Envío a toda España."
        path="/"
        image="/products/frutos-rojos.webp"
        type="website"
      />
      {/* Hero */}
      <section className="relative min-h-[86vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3850382/pexels-photo-3850382.jpeg"
            alt="Mermeladas artesanas Mimes Gourmet sobre mesa rústica"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-primary)]/85 via-[color:var(--brand-primary)]/25 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24">
          <div className="max-w-2xl text-[color:var(--brand-bg)] animate-fade-up">
            <p className="eyebrow text-[color:var(--brand-terracotta)] mb-6">Graus · Pirineo Aragonés</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
              Mermeladas artesanas
              <br />
              <span className="italic text-[color:var(--brand-terracotta)]">para mimar</span>
              <br /> tus sentidos
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[color:var(--brand-bg)]/85 max-w-lg">
              Elaboradas en lotes pequeños con fruta fresca, azúcar de caña y una paciencia infinita. Sabores únicos, únicos como cada tarro.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/tienda">
                <Button
                  data-testid="hero-cta-shop"
                  className="bg-[color:var(--brand-bg)] hover:bg-white text-[color:var(--brand-primary)] rounded-md px-7 py-6 font-medium tracking-wide"
                >
                  Ver la colección
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/historia">
                <Button
                  variant="outline"
                  data-testid="hero-cta-story"
                  className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-md px-7 py-6"
                >
                  Nuestra historia
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
        {[
          { icon: Sparkles, title: "Lotes pequeños", text: "Elaboración cuidada, sin prisa para mantener el color y sabor de la fruta." },
          { icon: Leaf, title: "Ingredientes naturales", text: "70% de fruta mínimo, azúcar de caña y limón. Sin conservantes." },
          { icon: MapPin, title: "Del Pirineo a tu mesa", text: "Frutas y sabores auténticos del Pirineo aragonés." },
        ].map((v, i) => (
          <div key={i} className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sage)]/15 flex items-center justify-center shrink-0">
              <v.icon className="w-4 h-4 text-[color:var(--brand-sage)]" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-[color:var(--brand-primary)]">{v.title}</h3>
              <p className="text-sm mt-1 leading-relaxed text-[color:var(--brand-neutral)]">{v.text}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Best Sellers */}
      <section data-testid="bestsellers-section" className="bg-[color:var(--brand-bg-alt)]/60 py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="eyebrow mb-3">Los más mimados</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-primary)] font-light tracking-tight">
                Nuestros <span className="italic">favoritos</span>
              </h2>
            </div>
            <Link to="/tienda" data-testid="bestsellers-view-all" className="text-sm text-[color:var(--brand-primary)] hover:text-[color:var(--brand-terracotta)] flex items-center gap-1 transition">
              Ver toda la tienda <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10">
            {bestsellers.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="relative aspect-[4/5] overflow-hidden rounded-md">
          <img
            src="https://images.pexels.com/photos/18100810/pexels-photo-18100810.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=940"
            alt="Paisaje del Pirineo aragonés"
            className="w-full h-full object-cover"
          />
          <div
            data-testid="artisan-badge-story"
            className="absolute bottom-5 right-5 sm:bottom-7 sm:right-7 w-24 sm:w-28 lg:w-32 aspect-square rounded-full bg-[color:var(--brand-bg)] shadow-[0_8px_28px_rgba(0,0,0,0.25)] ring-1 ring-black/5 flex items-center justify-center p-2 sm:p-2.5"
            title="Artesanía Alimentaria de Aragón"
            aria-label="Certificado Artesanía Alimentaria de Aragón"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_48158d91-3819-4c82-9472-12e6defd05aa/artifacts/1wz5fvfi_artesania%20alimentaria.png"
              alt="Sello Artesanía Alimentaria de Aragón"
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
        </div>
        <div>
          <p className="eyebrow mb-4">Desde Graus, Huesca</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-primary)] font-light tracking-tight leading-tight">
            Una cocina, un cazo <br />
            <span className="italic text-[color:var(--brand-terracotta)]">y mucho mimo.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[color:var(--brand-neutral)]">
            Nacimos en una cocina familiar del Pirineo con una idea sencilla: recuperar el sabor real de la fruta. Cada semana
            elegimos ingredientes de temporada, los cocinamos a fuego lento en tandas pequeñas y numeramos cada tarro.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[color:var(--brand-neutral)]">
            Combinamos recetas tradicionales con maridajes atrevidos: cebolla y trufa negra, membrillo con café, manzana y menta…
            Piezas únicas para regalar, para tu despensa o para descubrir con quienes quieres.
          </p>
          <Link to="/historia" className="inline-flex items-center gap-1 mt-6 text-sm font-medium text-[color:var(--brand-primary)] hover:text-[color:var(--brand-terracotta)]">
            Conoce toda la historia <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <p className="eyebrow text-[color:var(--brand-terracotta)] mb-6">Lo que dicen</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-8">
            {[
              { q: "La de cebolla con trufa es adictiva. La usé para acompañar un foie y fue el hit de la cena.", a: "— Elena R., Zaragoza" },
              { q: "Sabor a fruta de verdad. Se nota que es artesanal, nada que ver con lo del súper.", a: "— Marc B., Barcelona" },
              { q: "Pedí un pack de regalo y llegó impecable. Repetiré seguro.", a: "— Sofía L., Madrid" },
            ].map((t, i) => (
              <blockquote key={i} className="text-left">
                <p className="font-serif text-2xl leading-snug italic">&ldquo;{t.q}&rdquo;</p>
                <footer className="mt-4 text-sm text-[color:var(--brand-bg)]/70">{t.a}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
